import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  FaArrowRight,
  FaCalendarCheck,
  FaChevronDown,
  FaClipboardList,
  FaComments,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaRocket,
} from 'react-icons/fa';
import ScrollButton from "../components/ScrollButton";
import Navbar from '../components/Navbar';
import BottomContent from '../components/BottomContent';
import CountryCode from '../data/CountryCode';
import ContactImg from '../assets/img/ContactImg.png';
import DarkModeButton from '../components/DarkModeButton';
import Thank from '../components/Thank';
import './Contact.css';

const nextSteps = [
  {
    icon: <FaClipboardList />,
    title: 'We prepare a proposal',
    text: 'According to your product requirements, we prepare a clear proposal for you.',
  },
  {
    icon: <FaComments />,
    title: 'Together we discuss',
    text: 'We schedule a virtual or physical meeting to review the proposal with you or your team.',
  },
  {
    icon: <FaRocket />,
    title: 'Start building',
    text: 'After finalizing requirements, methods, and commercials, we start building your product.',
  },
];

const contactCards = [
  { icon: <FaEnvelope />, title: 'Write to us', text: 'Share your requirements and our team will respond quickly.' },
  { icon: <FaPhoneAlt />, title: 'Talk to team', text: 'Discuss your idea, product, support need, or transformation plan.' },
  { icon: <FaMapMarkerAlt />, title: 'Work with us', text: 'Collaborate with a technology partner across AI, cloud, and digital engineering.' },
];

const getCountryFlagUrl = (countryCode) =>
  `https://flagcdn.com/24x18/${countryCode.toLowerCase()}.png`;

function CountrySelect({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const selectedCountry = CountryCode.find(
    (country) => `${country.name} (${country.dial_code})` === value
  );
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredCountries = CountryCode
    .filter((country) => {
      if (!normalizedQuery) return true;
      return (
        country.name.toLowerCase().includes(normalizedQuery) ||
        country.dial_code.includes(normalizedQuery) ||
        country.code.toLowerCase().includes(normalizedQuery)
      );
    })
    .sort((firstCountry, secondCountry) => {
      if (!normalizedQuery) {
        return firstCountry.name.localeCompare(secondCountry.name);
      }

      const firstStartsWith = firstCountry.name.toLowerCase().startsWith(normalizedQuery);
      const secondStartsWith = secondCountry.name.toLowerCase().startsWith(normalizedQuery);
      if (firstStartsWith !== secondStartsWith) return firstStartsWith ? -1 : 1;
      return firstCountry.name.localeCompare(secondCountry.name);
    });

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, []);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    } else {
      setSearchQuery('');
    }
  }, [isOpen]);

  const selectCountry = (country) => {
    onChange(`${country.name} (${country.dial_code})`);
    setIsOpen(false);
  };

  return (
    <div className="contact-country-select" ref={containerRef}>
      <button
        type="button"
        id="countrycode"
        className="form-control contact-country-trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setIsOpen(false);
        }}
      >
        <span className={selectedCountry ? '' : 'contact-country-placeholder'}>
          {selectedCountry && (
            <img
              src={getCountryFlagUrl(selectedCountry.code)}
              alt=""
              width="24"
              height="18"
            />
          )}
          {selectedCountry
            ? `${selectedCountry.name} (${selectedCountry.dial_code})`
            : '--Select Country--'}
        </span>
        <FaChevronDown aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="contact-country-menu" role="listbox" aria-labelledby="country-label">
          <div className="contact-country-search-wrap">
            <input
              ref={searchInputRef}
              type="search"
              className="contact-country-search"
              value={searchQuery}
              placeholder="Search country or code..."
              aria-label="Search country"
              onChange={(event) => setSearchQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') setIsOpen(false);
                if (event.key === 'Enter' && filteredCountries.length) {
                  event.preventDefault();
                  selectCountry(filteredCountries[0]);
                }
              }}
            />
          </div>
          <div className="contact-country-options">
            {filteredCountries.map((country) => {
              const countryValue = `${country.name} (${country.dial_code})`;
              return (
                <button
                  type="button"
                  role="option"
                  aria-selected={value === countryValue}
                  className="contact-country-option"
                  key={country.code}
                  onClick={() => selectCountry(country)}
                >
                  <img
                    src={getCountryFlagUrl(country.code)}
                    alt=""
                    width="24"
                    height="18"
                    loading="lazy"
                  />
                  <span>{country.name} ({country.dial_code})</span>
                </button>
              );
            })}
            {!filteredCountries.length && (
              <p className="contact-country-empty">No country found</p>
            )}
          </div>
        </div>
      )}
      <input type="hidden" name="countrycode" value={value} />
    </div>
  );
}

export default function Contact() {
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [countrycode, setCountryCode] = useState('');
  const [number, setNumber] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = () => {
    const isMobileValid = /^\d{10}$/.test(number);
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

    if (!isMobileValid || !isEmailValid) {
      setError(true);
    } else {
      const url = "https://vedalytics.com/contactData.php";
      let fData = new FormData();
      fData.append('firstname', firstname);
      fData.append('lastname', lastname);
      fData.append('countrycode', countrycode);
      fData.append('number', number);
      fData.append('email', email);
      fData.append('company', company);
      fData.append('role', role);
      fData.append('message', message);

      axios.post(url, fData)
        .then(() => {
          Swal.fire({
            icon: 'success',
            title: 'Success!',
            text: 'Your form has been submitted successfully!',
            showConfirmButton: false,
            timer: 3000
          });

          setFirstname('');
          setLastname('');
          setCountryCode('');
          setNumber('');
          setEmail('');
          setCompany('');
          setRole('');
          setMessage('');
          setError(false);
        })
        .catch(() => {
          Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: 'Something went wrong! Please try again later.',
          });
        });
    }
  };

  return (
    <>
      <Navbar />

      <main className="contact-page">
        <section className="contact-hero">
          <div className="contact-hero-bg" style={{ backgroundImage: `url(${ContactImg})` }}></div>
          <div className="contact-hero-overlay"></div>
          <div className="container contact-hero-inner">
            <div className="contact-hero-copy">
              <span className="contact-eyebrow">Contact Vedalytics</span>
              <h1>Let’s shape your next intelligent product.</h1>
              <p>
                We’re here to help. Whether you have questions or need support
                for your next project, share your requirements and our team will
                respond as soon as possible.
              </p>
              <div className="contact-hero-actions">
                <a href="#contact-form" className="contact-btn contact-btn-primary">
                  Start a conversation <FaArrowRight />
                </a>
                <a href="#next-steps" className="contact-btn contact-btn-secondary">
                  View process
                </a>
              </div>
            </div>

            <div className="contact-command-card">
              <div className="contact-command-header">
                <FaCalendarCheck />
                <span>Response workflow</span>
              </div>
              <div className="contact-command-metric">
                <strong>3 Steps</strong>
                <p>Proposal, discussion, and product build planning.</p>
              </div>
              <div className="contact-command-list">
                <span>AI & digital product consulting</span>
                <span>Cloud, DevOps, and application engineering</span>
                <span>Research-led product innovation</span>
              </div>
            </div>
          </div>
        </section>

        <section className="contact-info-band">
          <div className="container">
            <div className="row g-4">
              {contactCards.map((item) => (
                <div className="col-md-4" key={item.title}>
                  <article className="contact-info-card">
                    <div className="contact-info-icon">{item.icon}</div>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="contact-main-section" id="contact-form">
          <div className="container">
            <div className="row gy-5 align-items-start">
              <div className="col-lg-5" id="next-steps">
                <div className="contact-process-panel">
                  <span className="contact-eyebrow">What will be the next step?</span>
                  <h2>You are one step away from building your perfect product.</h2>
                  <div className="contact-step-list">
                    {nextSteps.map((step, index) => (
                      <div className="contact-step" key={step.title}>
                        <div className="contact-step-icon">{step.icon}</div>
                        <div>
                          <small>{String(index + 1).padStart(2, '0')}</small>
                          <h3>{step.title}</h3>
                          <p>{step.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="col-lg-7">
                <div className="contact-form-card">
                  <div className="contact-form-heading">
                    <span className="contact-eyebrow">Tell us about your requirements</span>
                    <h2>We will discuss the right solution with you.</h2>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="firstname">First name</label>
                      <input id="firstname" type="text" className="form-control" placeholder="Your first name" value={firstname} onChange={(e) => setFirstname(e.target.value)} />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="lastname">Last name</label>
                      <input id="lastname" type="text" className="form-control" placeholder="Your last name" value={lastname} onChange={(e) => setLastname(e.target.value)} />
                    </div>
                    <div className="col-12">
                      <label id="country-label" htmlFor="countrycode">Country</label>
                      <CountrySelect value={countrycode} onChange={setCountryCode} />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="number">Mobile number<span className="contact-required">*</span></label>
                      <input
                        id="number"
                        type="tel"
                        className="form-control"
                        placeholder="10-digit mobile number"
                        value={number}
                        inputMode="numeric"
                        maxLength={10}
                        pattern="[0-9]{10}"
                        onChange={(e) => setNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        required
                      />
                      {error && !/^\d{10}$/.test(number) ? (
                        <span className="contact-error">Enter a valid 10-digit mobile number</span>
                      ) : ''}
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="email">Email ID<span className="contact-required">*</span></label>
                      <input id="email" type="email" className="form-control" placeholder="Your email id" value={email} onChange={(e) => setEmail(e.target.value)} required />
                      {error && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? (
                        <span className="contact-error">Enter a valid email address</span>
                      ) : ''}
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="company">Company</label>
                      <input id="company" type="text" className="form-control" placeholder="Your company name" value={company} onChange={(e) => setCompany(e.target.value)} />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="role">Current role</label>
                      <input id="role" type="text" className="form-control" placeholder="Your current role" value={role} onChange={(e) => setRole(e.target.value)} />
                    </div>
                    <div className="col-12">
                      <label htmlFor="message">Message</label>
                      <textarea id="message" name="message" className="form-control" rows="5" placeholder="Tell us what you want to build" value={message} onChange={(e) => setMessage(e.target.value)}></textarea>
                    </div>
                  </div>

                  <button className="contact-submit-btn" onClick={handleSubmit}>
                    Submit request <FaArrowRight />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Thank />
      <BottomContent />
      <DarkModeButton />
      <ScrollButton />
    </>
  );
}
